import { useCrudService } from 'framework/crud';
import { DEFAULT_ERROR_MESSAGE } from 'framework/form';
import type { LoadingParams } from 'framework/list';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { useTechnologyService } from 'domain/shared/career';
import { useBusinessFunctionService } from 'domain/shared/businessFunction';
import { useOfficeService, usePositionService } from 'domain/shared/employee';
import { usePeopleManagementApi } from 'infrastructure/api';
import type { PositionSlot, PositionSlotListItem } from 'domain/people/positionSlots/types';
import { enrichPositionSlots } from 'domain/people/positionSlots/utils/positionSlotDisplay';
import {
  WORKFORCE_ANALYTICS_INCLUDED_POSITION_SLOT_STATUSES,
  WORKFORCE_ANALYTICS_POSITION_SLOT_PAGE_SIZE,
} from 'domain/people/workforceAnalytics/constants';

const POSITION_SLOT_RESOURCE_URL = '/position-slots';

const useWorkforceAnalyticsService = () => {
  const { addMessage } = useMessageService();
  const { findAll: findAllTechnologies } = useTechnologyService();
  const { findAll: findAllPositions } = usePositionService();
  const { findAll: findAllOffices } = useOfficeService();
  const { findAll: findAllBusinessFunctions } = useBusinessFunctionService();
  const { searchAll } = useCrudService<PositionSlot>({
    listResourceUrl: POSITION_SLOT_RESOURCE_URL,
    singleResourceUrl: `${POSITION_SLOT_RESOURCE_URL}/:id`,
    useService: usePeopleManagementApi,
  });

  const getWorkforceAnalyticsPositionSlots = async (
    loadParams: Partial<LoadingParams> = {},
  ): Promise<PositionSlotListItem[]> => {
    try {
      const { filter = {}, ...restLoadParams } = loadParams;
      const [positionSlots, positions, technologies, offices, businessFunctions] = await Promise.all([
        searchAll({
          ...restLoadParams,
          filter: {
            ...filter,
            status: {
              in: WORKFORCE_ANALYTICS_INCLUDED_POSITION_SLOT_STATUSES,
            },
          },
          pageSize: WORKFORCE_ANALYTICS_POSITION_SLOT_PAGE_SIZE,
        }),
        findAllPositions(),
        findAllTechnologies(),
        findAllOffices(),
        findAllBusinessFunctions(),
      ]);

      return enrichPositionSlots(positionSlots || [], {
        positions,
        technologies,
        offices,
        businessFunctions,
      });
    } catch {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));

      return [];
    }
  };

  const getWorkforceAnalyticsPositionSlotsListData = async (loadParams: LoadingParams) => {
    const positionSlots = await getWorkforceAnalyticsPositionSlots(loadParams);

    return {
      result: positionSlots,
      totalElements: positionSlots.length,
    };
  };

  return {
    getWorkforceAnalyticsPositionSlots,
    getWorkforceAnalyticsPositionSlotsListData,
  };
};

export default useWorkforceAnalyticsService;
